// exp_PORTFOLIO-61587_file
import { ChangeEventHandler, FC } from 'react';

import { formatCost } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { getVacancyOfTheDayForecast, RUBLES_PER_CLICK, VacancyOfTheDayPresetId } from 'utils/vacancyOfTheDay';

import vacancyOfTheDayFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vacancy_of_the_day/vacancy_of_the_day_form_field_touch';
import { CheckableCard, Flex, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { CustomClicksAndBudget } from 'components/VacancyOfTheDayBuilder/components/CustomClicksAndBudget/CustomClicksAndBudget';
import { useVacancyOfTheDayAnalyticsParams } from 'components/VacancyOfTheDayBuilder/useVacancyOfTheDayAnalyticsParams';

import {
    selectVacancyOfTheDayPreset,
    selectVacancyOfTheDayPresets,
    setVacancyOfTheDayBudget,
    setVacancyOfTheDayClicks,
    setVacancyOfTheDayErrors,
    setVacancyOfTheDayPreset,
} from 'store/models/vacancyOfTheDay';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/VacancyOfTheDayBuilder/components/Presets.module.less';

const TrlKeys = {
    title: 'VacancyOfTheDayPage.cost.title',
    description: 'VacancyOfTheDayPage.cost.description',
    budget: 'VacancyOfTheDayPage.presets.budget',
    clicks: 'VacancyOfTheDayPage.presets.clicks',
    custom: 'VacancyOfTheDayPage.presets.custom',
};

const PRESET_ORDER = [
    VacancyOfTheDayPresetId.Small,
    VacancyOfTheDayPresetId.Medium,
    VacancyOfTheDayPresetId.Large,
    VacancyOfTheDayPresetId.Custom,
];

export const Presets: FC = () => {
    const preset = useSelector(selectVacancyOfTheDayPreset);
    const presets = useSelector(selectVacancyOfTheDayPresets);

    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();
    const { params } = useVacancyOfTheDayAnalyticsParams();

    const handleChangePreset: ChangeEventHandler<HTMLInputElement> = (event) => {
        const nextPreset = event.target.value as VacancyOfTheDayPresetId;
        const amount = nextPreset === VacancyOfTheDayPresetId.Custom ? presets.SMALL : presets[nextPreset];

        dispatch([
            setVacancyOfTheDayPreset(nextPreset),
            setVacancyOfTheDayClicks(amount.clicks),
            setVacancyOfTheDayBudget(amount.budget),
            setVacancyOfTheDayErrors({ clicks: false, budget: false }),
        ]);
        vacancyOfTheDayFormFieldTouch({
            ...params,
            fieldName: 'preset',
            preset: nextPreset,
            budget: amount.budget,
            clicks: amount.clicks,
            impressions: getVacancyOfTheDayForecast(amount.clicks).impressions,
            geoIds: null,
            geoSource: 'None',
            roleIds: null,
            roleSource: 'None',
        });
    };

    return (
        <div>
            <Text typography="label-2-regular">{trls(TrlKeys.title)}</Text>
            <VSpacing default={4} />
            <Text typography="label-2-regular" style="secondary">
                {trls(TrlKeys.description, [RUBLES_PER_CLICK])}
            </Text>
            <VSpacing default={12} />
            <Flex direction="row" wrap={isMobile ? 'wrap' : 'nowrap'} gap={12}>
                {PRESET_ORDER.map((presetId) => {
                    const amount = presetId === VacancyOfTheDayPresetId.Custom ? null : presets[presetId];

                    return (
                        <Flex key={presetId} grow={1} basis={isMobile ? 'calc(50% - 6px)' : 0}>
                            <CheckableCard
                                flexible
                                padding={16}
                                borderRadius={16}
                                type="radio"
                                name="vacancy-of-the-day-presets"
                                value={presetId}
                                checked={preset === presetId}
                                onChange={handleChangePreset}
                                data-qa={`vacancy-of-the-day-preset-${presetId.toLowerCase()}`}
                            >
                                <Flex gap={8} direction="column">
                                    <Text typography="subtitle-1-semibold">
                                        {amount ? (
                                            trls(TrlKeys.budget, [formatCost(amount.budget, NON_BREAKING_SPACE)])
                                        ) : (
                                            <span className={styles.customLabel}>{trls(TrlKeys.custom)}</span>
                                        )}
                                    </Text>
                                    {amount && (
                                        <Text typography="label-3-regular" style="secondary">
                                            {trls(TrlKeys.clicks, [amount.clicks])}
                                        </Text>
                                    )}
                                </Flex>
                            </CheckableCard>
                        </Flex>
                    );
                })}
            </Flex>
            {preset === VacancyOfTheDayPresetId.Custom && <CustomClicksAndBudget />}
        </div>
    );
};
